import { Medialink } from '../../../../api/medialink';
import { SlideSet } from '../../../../api/slide-set';
import { NodeWithoutDescription } from '../../../../api/table-of-content';
import { UserMaterial } from '../../../../api/user-material';
import { groupMedialinksByNode } from '../../../../utils/medialink';
import { flattenNodes } from '../../../../utils/tocNode';
import { SharedMaterial } from '../context/QueryMaterialContext';

const groupUserMaterialByNode = (userMaterials: UserMaterial[]) => {
  return userMaterials.reduce<Record<string, UserMaterial[]>>((groupedUserMaterials, userMaterial) => {
    return {
      ...groupedUserMaterials,
      [userMaterial.node]: [...(groupedUserMaterials[userMaterial.node] || []), userMaterial],
    };
  }, {});
};

const slideSetByNode = (slideSets: SlideSet[]) => {
  return slideSets.reduce<Record<string, SlideSet[]>>((groupedSlideSets, slideSet) => {
    return {
      ...groupedSlideSets,
      [slideSet.nodeId]: [...(groupedSlideSets[slideSet.nodeId] || []), slideSet],
    };
  }, {});
};

const groupSharedMaterialByNode = (sharedMaterial: SharedMaterial[]) => {
  return sharedMaterial.reduce<Record<string, SharedMaterial[]>>((groupedSharedMaterial, material) => {
    const nodeId = 'nodeId' in material ? material.nodeId : material.node;
    return {
      ...groupedSharedMaterial,
      [nodeId]: [...(groupedSharedMaterial[nodeId] || []), material],
    };
  }, {});
};

export function groupResultsByModule(
  moduleIds: string[],
  medialinks: Medialink[] = [],
  userMaterial: UserMaterial[] = [],
  slideSets: SlideSet[] = [],
  sharedMaterial: SharedMaterial[] = [],
  nodesByModule: Record<string, NodeWithoutDescription[]>,
): {
  moduleId: string;
  count: number;
  nodes: NodeWithoutDescription[];
  groupedByNode: {
    [nodeId: string]: {
      medialinks: Medialink[];
      userMaterial: UserMaterial[];
      slideSets: SlideSet[];
      sharedMaterial: SharedMaterial[];
    };
  };
}[] {
  const flattenedNodesByModule: Record<string, NodeWithoutDescription[]> = {};

  moduleIds.forEach(moduleId => {
    flattenedNodesByModule[moduleId] = flattenNodes(nodesByModule[moduleId] || []);
  });

  return moduleIds.reduce<
    {
      moduleId: string;
      count: number;
      nodes: NodeWithoutDescription[];
      groupedByNode: {
        [nodeId: string]: {
          medialinks: Medialink[];
          userMaterial: UserMaterial[];
          slideSets: SlideSet[];
          sharedMaterial: SharedMaterial[];
        };
      };
    }[]
  >((acc, moduleId) => {
    const medialinksForModule = medialinks.filter(medialink => medialink.moduleId === moduleId);
    const medialinksByNode = groupMedialinksByNode(medialinksForModule);

    const userMaterialForModule = userMaterial.filter(x => x.moduleId === moduleId);
    const userMaterialByNode = groupUserMaterialByNode(userMaterialForModule);

    const slideSetsForModule = slideSets.filter(x => x.moduleId === moduleId);
    const slideSetsByNode = slideSetByNode(slideSetsForModule);

    const sharedMaterialsForModule = sharedMaterial.filter(x => x.moduleId === moduleId);
    const sharedMaterialsByNode = groupSharedMaterialByNode(sharedMaterialsForModule);

    const materialNodeIds = [
      ...Object.keys(sharedMaterialsByNode),
      ...Object.keys(slideSetsByNode),
      ...Object.keys(userMaterialByNode),
      ...Object.keys(medialinksByNode),
    ];

    const orderedNodeIds = flattenedNodesByModule[moduleId].map(x => x.id).filter(x => materialNodeIds.includes(x));

    if (
      medialinksForModule.length > 0 ||
      userMaterialForModule.length > 0 ||
      slideSetsForModule.length > 0 ||
      sharedMaterialsForModule.length > 0
    ) {
      return [
        ...acc,
        {
          moduleId,
          count:
            medialinksForModule.length +
            userMaterialForModule.length +
            slideSetsForModule.length +
            sharedMaterialsForModule.length,
          nodes: flattenedNodesByModule[moduleId],
          groupedByNode: orderedNodeIds.reduce<
            Record<
              string,
              {
                medialinks: Medialink[];
                userMaterial: UserMaterial[];
                slideSets: SlideSet[];
                sharedMaterial: SharedMaterial[];
              }
            >
          >((prev, nodeId) => {
            return {
              ...prev,
              [nodeId]: {
                medialinks: medialinksByNode[nodeId] || [],
                userMaterial: userMaterialByNode[nodeId] || [],
                slideSets: slideSetsByNode[nodeId] || [],
                sharedMaterial: sharedMaterialsByNode[nodeId] || [],
              },
            };
          }, {}),
        },
      ];
    }

    return acc;
  }, []);
}
